import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Spinner } from '../ui/spinner';
import { Alert, AlertDescription } from '../ui/alert';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Download, Pencil, Plus, Trash2 } from 'lucide-react';
import { crmCampaignsAPI } from '../../services/api';
import { downloadLiveCasinoGamesCsv } from './cashbackWeekendExport';

// currency is varchar(10) in Redshift, which counts bytes.
const MAX_CURRENCY_BYTES = 10;
const byteLength = (s) => new TextEncoder().encode(s).length;
const isValidCurrency = (s) => {
  const c = s.trim();
  return c !== '' && !/\s/.test(c) && byteLength(c) <= MAX_CURRENCY_BYTES;
};

// The amounts are numeric(38, 20): up to 18 integer and 20 fractional digits. They stay strings
// end to end so nothing is rounded; the backend checks min <= max exactly, this is just a hint.
const isValidAmount = (s) => /^\d{1,18}(\.\d{1,20})?$/.test(s.trim());

const apiError = (err, fallback) => err?.response?.data?.error || fallback;

// The promo times are CET/CEST wall-clock times with no zone attached. The backend sends and
// takes them as "YYYY-MM-DDTHH:MM:SS", exactly what a datetime-local input holds, so they are
// never converted to or from the viewer's own time zone.
// Browsers drop ":00" seconds from the input's value, so pad it back to keep one format — that
// keeps the dirty check honest and lets values compare as strings.
const toInputValue = (v) => (v ? v.slice(0, 19) : '');
const withSeconds = (v) => (v.length === 16 ? `${v}:00` : v);
const FieldLabel = ({ children }) => (
  <span className="font-mono text-[10px] uppercase tracking-wider text-ink-mute">{children}</span>
);

const SectionTitle = ({ title }) => <h3 className="text-base font-bold text-ink">{title}</h3>;

// The timeframe table holds exactly one row: the form edits it in place, or creates it the
// first time.
function TimeframeCard() {
  const [timeframe, setTimeframe] = useState(null);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const apply = (tf) => {
    setTimeframe(tf);
    setStart(toInputValue(tf?.promo_start));
    setEnd(toInputValue(tf?.promo_end));
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await crmCampaignsAPI.getCashbackTimeframe();
        if (!res.data || typeof res.data !== 'object') throw new Error('unexpected response');
        apply(res.data.timeframe);
      } catch (err) {
        setError(apiError(err, 'Failed to load the promotion window.'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const dirty = start !== toInputValue(timeframe?.promo_start) || end !== toInputValue(timeframe?.promo_end);
  const endBeforeStart = start && end && end <= start;
  const valid = start && end && !endBeforeStart;

  const save = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const res = await crmCampaignsAPI.setCashbackTimeframe({ promo_start: start, promo_end: end });
      apply(res.data);
      setSaved(true);
    } catch (err) {
      setError(apiError(err, 'Failed to save the promotion window.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-5 space-y-4">
        <SectionTitle title="Promotion window" />
        {loading ? (
          <div className="flex justify-center py-4"><Spinner /></div>
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-3">
              <label className="space-y-1.5 block">
                <FieldLabel>Promo start (CET/CEST)</FieldLabel>
                <Input type="datetime-local" step="1" className="h-9 w-60 text-sm" value={start}
                  onChange={(e) => { setStart(withSeconds(e.target.value)); setSaved(false); }} />
              </label>
              <label className="space-y-1.5 block">
                <FieldLabel>Promo end (CET/CEST)</FieldLabel>
                <Input type="datetime-local" step="1" className="h-9 w-60 text-sm" value={end}
                  onChange={(e) => { setEnd(withSeconds(e.target.value)); setSaved(false); }} />
              </label>
              <Button size="sm" className="h-9" onClick={save} disabled={!valid || !dirty || saving}>
                {saving ? 'Saving…' : timeframe ? 'Save window' : 'Set window'}
              </Button>
              {dirty && timeframe && (
                <Button variant="outline" size="sm" className="h-9" onClick={() => apply(timeframe)} disabled={saving}>
                  Reset
                </Button>
              )}
            </div>
            {endBeforeStart && <p className="text-xs font-medium text-negative">The end must be after the start.</p>}
            {!timeframe && !error && (
              <p className="text-xs font-medium text-ink-dim">No window is set yet.</p>
            )}
            {saved && !dirty && <p className="text-xs font-medium text-ink-mute">Saved.</p>}
          </>
        )}
        {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}
      </CardContent>
    </Card>
  );
}

// Add and edit share one form. When editing, the currency is the row's identity and stays
// read-only — renaming one means deleting it and adding it again.
function LimitFormDialog({ open, initial, onClose, onSaved }) {
  const editing = Boolean(initial);
  const [currency, setCurrency] = useState('');
  const [minCashback, setMinCashback] = useState('');
  const [maxCashback, setMaxCashback] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setCurrency(initial?.currency || '');
    setMinCashback(initial?.min_cashback || '');
    setMaxCashback(initial?.max_cashback || '');
    setError('');
  }, [open, initial]);

  const amountsValid = isValidAmount(minCashback) && isValidAmount(maxCashback);
  const minAboveMax = amountsValid && Number(minCashback) > Number(maxCashback);
  const valid = isValidCurrency(currency) && amountsValid && !minAboveMax;

  const submit = async () => {
    setSaving(true);
    setError('');
    const body = { min_cashback: minCashback.trim(), max_cashback: maxCashback.trim() };
    try {
      if (editing) {
        await crmCampaignsAPI.updateCashbackLimit(initial.currency, body);
      } else {
        await crmCampaignsAPI.createCashbackLimit({ ...body, currency: currency.trim() });
      }
      onSaved();
    } catch (err) {
      setError(apiError(err, 'Failed to save limit.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit limit' : 'Add limit'}</DialogTitle>
          <DialogDescription>A currency can have one limit.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <label className="space-y-1.5 block">
            <FieldLabel>Currency</FieldLabel>
            <Input value={currency} disabled={editing} placeholder="e.g. EUR"
              onChange={(e) => setCurrency(e.target.value)} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1.5 block">
              <FieldLabel>Min cashback</FieldLabel>
              <Input inputMode="decimal" value={minCashback} onChange={(e) => setMinCashback(e.target.value)} />
            </label>
            <label className="space-y-1.5 block">
              <FieldLabel>Max cashback</FieldLabel>
              <Input inputMode="decimal" value={maxCashback} onChange={(e) => setMaxCashback(e.target.value)} />
            </label>
          </div>
          {minAboveMax && <p className="text-xs font-medium text-negative">Min cashback must not exceed max cashback.</p>}
          {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={!valid || saving}>
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Add limit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ row, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setError(''); }, [row]);

  const confirm = async () => {
    setDeleting(true);
    try {
      await crmCampaignsAPI.deleteCashbackLimit(row.currency);
      onDeleted();
    } catch (err) {
      setError(apiError(err, 'Failed to delete limit.'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={Boolean(row)} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete limit?</DialogTitle>
          <DialogDescription>
            {row && `The ${row.currency} cashback limit. This cannot be undone.`}
          </DialogDescription>
        </DialogHeader>
        {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting}>Cancel</Button>
          <Button variant="destructive" onClick={confirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LimitsSection() {
  const [limits, setLimits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await crmCampaignsAPI.listCashbackLimits();
      if (!Array.isArray(res.data?.limits)) throw new Error('unexpected response');
      setLimits(res.data.limits);
    } catch (err) {
      setError(apiError(err, 'Failed to load limits.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (row) => { setEditing(row); setFormOpen(true); };
  const closeForm = () => { setFormOpen(false); setEditing(null); };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <SectionTitle title="Cashback limits" />
        <Button size="sm" onClick={openAdd}>
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Add limit
        </Button>
      </div>

      {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-10"><Spinner /></div>
          ) : limits.length === 0 ? (
            <p className="py-10 text-center text-sm font-medium text-ink-dim">No limits yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Currency</TableHead>
                  <TableHead className="text-right">Min cashback</TableHead>
                  <TableHead className="text-right">Max cashback</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {limits.map((row) => (
                  <TableRow key={row.currency}>
                    <TableCell className="font-mono text-xs font-bold">{row.currency}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{row.min_cashback}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{row.max_cashback}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(row)} aria-label="Edit">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleting(row)} aria-label="Delete">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <LimitFormDialog
        open={formOpen}
        initial={editing}
        onClose={closeForm}
        onSaved={() => { closeForm(); load(); }}
      />
      <DeleteDialog
        row={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={() => { setDeleting(null); load(); }}
      />
    </div>
  );
}

// Fetches the cashback_live games from the warehouse and downloads them as a CSV.
function ExportGamesButton() {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const exportGames = async () => {
    setExporting(true);
    setError('');
    try {
      const res = await crmCampaignsAPI.listLiveCasinoGames();
      if (!Array.isArray(res.data?.games)) throw new Error('unexpected response');
      downloadLiveCasinoGamesCsv(res.data.games);
    } catch (err) {
      setError(apiError(err, 'Failed to export live casino games.'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-2">
      <Button variant="outline" size="sm" onClick={exportGames} disabled={exporting}>
        {exporting ? <Spinner size="sm" className="mr-1.5" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
        Export live_casino games
      </Button>
      {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}
    </div>
  );
}

const CashbackWeekendPanel = () => (
  <div className="space-y-6">
    <TimeframeCard />
    <LimitsSection />
    <ExportGamesButton />
  </div>
);

export default CashbackWeekendPanel;
